// src/scenes/dashboard/OwnerDashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Chip,
    Avatar,
    Stack,
    Divider,
    LinearProgress,
    Alert,
    useTheme,
} from "@mui/material";
import {
    Assessment as AssessmentIcon,
    SchoolOutlined as SchoolIcon,
    LocationOnOutlined as LocationIcon,
    FactCheckOutlined as InspectIcon,
    TaskAltOutlined as AnswerIcon,
} from "@mui/icons-material";
import {
    LineChart,
    Line,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    Tooltip as RTooltip,
    CartesianGrid,
    ResponsiveContainer,
    Legend,
} from "recharts";
import { alpha } from "@mui/material/styles";

/* =========================
   API ENDPOINTS (TABLE-NAMES)
   ========================= */
const API_ROOT = "https://figazuresql.swancreate.com/api/swa";
const URL_SCHOOLS = `${API_ROOT}/SchoolMst`;
const URL_INSPECTION_MST = `${API_ROOT}/InspectionMst`;
const URL_RESULT_DETAIL = `${API_ROOT}/InspectionResultDetail`;

/* =========================
   HELPERS
   ========================= */
async function safeFetch(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`${url} -> ${res.status} ${res.statusText}`);
    const ct = res.headers.get("content-type") || "";
    return ct.includes("application/json") ? res.json() : res.text();
}
function fmt(n) {
    return new Intl.NumberFormat().format(n || 0);
}
function toDateOnly(d) {
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(
        x.getDate()
    ).padStart(2, "0")}`;
}
function lastNDays(n) {
    const arr = [];
    const today = new Date();
    for (let i = n - 1; i >= 0; i--) {
        const d = new Date(today);
        d.setDate(today.getDate() - i);
        arr.push(toDateOnly(d));
    }
    return arr;
}
// Group a single performed inspection “session” by School + Inspection + Date
function deriveSessionKey(r) {
    const d = r.CreatedOn ? toDateOnly(r.CreatedOn) : "1970-01-01";
    const school = (r.CustomerID ?? "school0").toString();
    const insp = (r.inspection_apiid ?? "insp0").toString();
    return `${school}|${insp}|${d}`;
}

// Chart palette (used in pies/bars)
const CHART_COLORS = [
    "#1976d2",
    "#2e7d32",
    "#d32f2f",
    "#ed6c02",
    "#9c27b0",
    "#00897b",
    "#7b1fa2",
    "#5d4037",
    "#455a64",
    "#c2185b",
];

/* =========================
   PAGE
   ========================= */
export default function OwnerDashboard() {
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Adaptive chart colors for light/dark mode
    const axisColor = isDark
        ? alpha(theme.palette.common.white, 0.8)
        : alpha(theme.palette.common.black, 0.75);
    const gridColor = isDark
        ? alpha(theme.palette.common.white, 0.18)
        : alpha(theme.palette.common.black, 0.1);
    const tooltipBg = isDark ? alpha("#000", 0.9) : "#fff";
    const tooltipBorder = isDark ? alpha("#fff", 0.2) : alpha("#000", 0.2);
    const tooltipText = isDark ? "#fff" : "#111";
    const legendStyle = { color: axisColor, fontSize: 12 };

    // 🔧 Force a bright line in dark mode; keep theme primary in light mode
    const brightLineDark = "#00E5FF"; // cyan A400 – high contrast on dark
    const lineStroke = isDark ? brightLineDark : theme.palette.primary.main;
    const glowStroke = isDark
        ? alpha(brightLineDark, 0.33)
        : alpha(theme.palette.primary.main, 0.28);

    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    const [schools, setSchools] = useState([]); // SchoolMst
    const [inspections, setInspections] = useState([]); // InspectionMst (for titles)
    const [results, setResults] = useState([]); // InspectionResultDetail rows

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                setLoading(true);
                setErr("");
                const [s, r, m] = await Promise.all([
                    safeFetch(URL_SCHOOLS),
                    safeFetch(URL_RESULT_DETAIL),
                    safeFetch(URL_INSPECTION_MST),
                ]);
                if (!alive) return;
                setSchools(Array.isArray(s) ? s : []);
                setResults(Array.isArray(r) ? r : []);
                setInspections(Array.isArray(m) ? m : []);
            } catch (e) {
                if (!alive) return;
                setErr(e.message || "Failed to load dashboard data.");
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => {
            alive = false;
        };
    }, []);

    // Map inspection_apiid -> title
    const inspTitle = useMemo(() => {
        const map = new Map();
        inspections.forEach((i) => map.set(String(i.apiid), i.InspectionTitle || "Inspection"));
        return map;
    }, [inspections]);

    /* ---------- Aggregations ---------- */

    // Unique sessions (School + Inspection + Day)
    const inspectionSessions = useMemo(() => {
        const map = new Map();
        for (const r of results) {
            const key = deriveSessionKey(r);
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    date: r.CreatedOn ? toDateOnly(r.CreatedOn) : "1970-01-01",
                    inspection_apiid: r.inspection_apiid,
                    inspectionTitle: inspTitle.get(String(r.inspection_apiid)) || "Inspection",
                    CustomerID: r.CustomerID,
                    CustomerName: r.CustomerName || `School #${r.CustomerID}`,
                    count: 0,
                });
            }
            map.get(key).count += 1; // number of answered questions in that session
        }
        return Array.from(map.values());
    }, [results, inspTitle]);

    // KPI metrics
    const inspectedSchoolIds = useMemo(
        () => new Set(results.map((r) => String(r.CustomerID))).size,
        [results]
    );
    const kpi = useMemo(
        () => ({
            schools: schools.length,
            inspectedSchools: inspectedSchoolIds,
            inspections: inspectionSessions.length,
            answers: results.length,
        }),
        [schools, inspectedSchoolIds, inspectionSessions, results]
    );

    // Inspections over last 30 days
    const last30 = lastNDays(30);
    const inspectionsByDay = useMemo(() => {
        const cnt = Object.fromEntries(last30.map((d) => [d, 0]));
        for (const s of inspectionSessions) {
            if (s.date in cnt) cnt[s.date] += 1;
        }
        return last30.map((d) => ({ date: d, inspections: cnt[d] }));
    }, [inspectionSessions]);

    // Top schools by inspections (sessions)
    const topSchools = useMemo(() => {
        const map = new Map();
        for (const s of inspectionSessions) {
            const name = s.CustomerName || `School #${s.CustomerID}`;
            map.set(name, (map.get(name) || 0) + 1);
        }
        const arr = Array.from(map.entries()).map(([name, inspections]) => ({ name, inspections }));
        arr.sort((a, b) => b.inspections - a.inspections);
        return arr.slice(0, 10);
    }, [inspectionSessions]);

    // Score breakdown from answers
    const scoreBreakdown = useMemo(() => {
        let pass = 0,
            fail = 0,
            yes = 0,
            no = 0,
            other = 0;
        for (const r of results) {
            const s = String(r.score || "").trim().toLowerCase();
            if (s === "pass") pass++;
            else if (s === "fail") fail++;
            else if (s === "yes") yes++;
            else if (s === "no") no++;
            else other++;
        }
        const parts = [
            { name: "Pass", value: pass, color: "#2e7d32" },
            { name: "Fail", value: fail, color: "#d32f2f" },
            { name: "Yes", value: yes, color: "#1b5e20" },
            { name: "No", value: no, color: "#c62828" },
            { name: "Other", value: other, color: "#546e7a" },
        ].filter((x) => x.value > 0);
        return parts.length ? parts : [{ name: "No Data", value: 1, color: "#90a4ae" }];
    }, [results]);

    // Reinspect rate (from isReinspect)
    const reinspectPie = useMemo(() => {
        let flagged = 0;
        for (const r of results) {
            if (r.isReinspect === 1 || r.isReinspect === true || String(r.isReinspect) === "true")
                flagged++;
        }
        const ok = Math.max(results.length - flagged, 0);
        const data = [
            { name: "Reinspect", value: flagged, color: "#ed6c02" },
            { name: "Clear", value: ok, color: "#1976d2" },
        ];
        return results.length ? data : [{ name: "No Data", value: 1, color: "#90a4ae" }];
    }, [results]);

    // Recent sessions (last 10)
    const recentSessions = useMemo(() => {
        const sorted = [...inspectionSessions].sort((a, b) =>
            a.date < b.date ? 1 : a.date > b.date ? -1 : 0
        );
        return sorted.slice(0, 10);
    }, [inspectionSessions]);

    /* ---------- UI helpers ---------- */
    const KpiCard = ({ icon, label, value, color }) => (
        <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent>
                <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: color, color: "#fff", width: 48, height: 48 }}>
                        {icon}
                    </Avatar>
                    <Box>
                        <Typography variant="overline" sx={{ letterSpacing: 1, opacity: 0.85 }}>
                            {label}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 800 }}>
                            {fmt(value)}
                        </Typography>
                    </Box>
                </Stack>
            </CardContent>
        </Card>
    );

    /* ---------- Render ---------- */
    return (
        <Box sx={{ p: 2 }}>
            <Card elevation={3} sx={{ borderRadius: 3, overflow: "hidden", mb: 2 }}>
                <CardHeader
                    title="Inspections Dashboard"
                    subheader="Overview of schools, inspection activity, and results"
                    sx={{
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        "& .MuiCardHeader-subheader": { color: "rgba(255,255,255,0.85)" },
                        pb: 1,
                    }}
                />
                {loading && <LinearProgress />}
                <CardContent>
                    {err && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {err}
                        </Alert>
                    )}

                    {/* KPIs */}
                    <Grid container spacing={2} sx={{ mb: 1 }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <KpiCard
                                icon={<SchoolIcon />}
                                label="Schools"
                                value={kpi.schools}
                                color={theme.palette.info.main}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KpiCard
                                icon={<LocationIcon />}
                                label="Inspected Schools"
                                value={kpi.inspectedSchools}
                                color={theme.palette.secondary.main}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KpiCard
                                icon={<InspectIcon />}
                                label="Inspections Performed"
                                value={kpi.inspections}
                                color={theme.palette.success.main}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3}>
                            <KpiCard
                                icon={<AnswerIcon />}
                                label="Answers Recorded"
                                value={kpi.answers}
                                color={theme.palette.warning.main}
                            />
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    {/* Charts */}
                    <Grid container spacing={2}>
                        {/* Inspections over time */}
                        <Grid item xs={12} md={8}>
                            <Card elevation={1} sx={{ borderRadius: 3, height: 360 }}>
                                <CardHeader
                                    title="Inspections — Last 30 Days"
                                    avatar={
                                        <Avatar sx={{ bgcolor: "primary.main" }}>
                                            <AssessmentIcon />
                                        </Avatar>
                                    }
                                />
                                <CardContent sx={{ height: 280 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart
                                            data={inspectionsByDay}
                                            margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
                                        >
                                            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="date"
                                                tick={{ fontSize: 12, fill: axisColor }}
                                                axisLine={{ stroke: axisColor }}
                                                tickLine={{ stroke: axisColor }}
                                            />
                                            <YAxis
                                                allowDecimals={false}
                                                tick={{ fontSize: 12, fill: axisColor }}
                                                axisLine={{ stroke: axisColor }}
                                                tickLine={{ stroke: axisColor }}
                                            />
                                            <RTooltip
                                                contentStyle={{
                                                    backgroundColor: tooltipBg,
                                                    borderColor: tooltipBorder,
                                                    color: tooltipText,
                                                }}
                                                labelStyle={{ color: tooltipText }}
                                                itemStyle={{ color: tooltipText }}
                                            />
                                            {/* Bright glow behind the main line */}
                                            <Line
                                                type="monotone"
                                                dataKey="inspections"
                                                stroke={glowStroke}
                                                strokeWidth={8}
                                                dot={false}
                                                isAnimationActive={false}
                                                strokeOpacity={1}
                                            />
                                            {/* FORCE bright stroke (dark) or theme primary (light) */}
                                            <Line
                                                type="monotone"
                                                dataKey="inspections"
                                                stroke={lineStroke}
                                                strokeWidth={3}
                                                dot={false}
                                                strokeOpacity={1}
                                                activeDot={{
                                                    r: 4,
                                                    strokeWidth: 2,
                                                    stroke: isDark ? "#001018" : theme.palette.primary.contrastText,
                                                    fill: lineStroke,
                                                }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Reinspect Rate */}
                        <Grid item xs={12} md={4}>
                            <Card elevation={1} sx={{ borderRadius: 3, height: 360 }}>
                                <CardHeader title="Reinspect Rate" />
                                <CardContent sx={{ height: 280 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <RTooltip
                                                contentStyle={{
                                                    backgroundColor: tooltipBg,
                                                    borderColor: tooltipBorder,
                                                    color: tooltipText,
                                                }}
                                                labelStyle={{ color: tooltipText }}
                                                itemStyle={{ color: tooltipText }}
                                            />
                                            <Legend wrapperStyle={legendStyle} />
                                            <Pie
                                                data={reinspectPie}
                                                dataKey="value"
                                                nameKey="name"
                                                outerRadius={100}
                                                innerRadius={60}
                                                paddingAngle={2}
                                            >
                                                {reinspectPie.map((entry, i) => (
                                                    <Cell key={`re-${i}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Top schools by inspections */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={1} sx={{ borderRadius: 3, height: 360 }}>
                                <CardHeader title="Top Schools by Inspections" />
                                <CardContent sx={{ height: 280 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart
                                            data={topSchools}
                                            margin={{ top: 10, right: 16, left: 0, bottom: 32 }}
                                        >
                                            <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
                                            <XAxis
                                                dataKey="name"
                                                angle={-20}
                                                textAnchor="end"
                                                interval={0}
                                                height={50}
                                                tick={{ fill: axisColor }}
                                                axisLine={{ stroke: axisColor }}
                                                tickLine={{ stroke: axisColor }}
                                            />
                                            <YAxis
                                                allowDecimals={false}
                                                tick={{ fill: axisColor }}
                                                axisLine={{ stroke: axisColor }}
                                                tickLine={{ stroke: axisColor }}
                                            />
                                            <RTooltip
                                                contentStyle={{
                                                    backgroundColor: tooltipBg,
                                                    borderColor: tooltipBorder,
                                                    color: tooltipText,
                                                }}
                                                labelStyle={{ color: tooltipText }}
                                                itemStyle={{ color: tooltipText }}
                                            />
                                            <Legend wrapperStyle={legendStyle} />
                                            <Bar dataKey="inspections" fill={theme.palette.info.main} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Score breakdown */}
                        <Grid item xs={12} md={6}>
                            <Card elevation={1} sx={{ borderRadius: 3, height: 360 }}>
                                <CardHeader title="Result Breakdown (Pass/Fail/Yes/No)" />
                                <CardContent sx={{ height: 280 }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <RTooltip
                                                contentStyle={{
                                                    backgroundColor: tooltipBg,
                                                    borderColor: tooltipBorder,
                                                    color: tooltipText,
                                                }}
                                                labelStyle={{ color: tooltipText }}
                                                itemStyle={{ color: tooltipText }}
                                            />
                                            <Legend wrapperStyle={legendStyle} />
                                            <Pie
                                                data={scoreBreakdown}
                                                dataKey="value"
                                                nameKey="name"
                                                outerRadius={100}
                                                innerRadius={60}
                                            >
                                                {scoreBreakdown.map((entry, i) => (
                                                    <Cell key={`sb-${i}`} fill={entry.color} />
                                                ))}
                                            </Pie>
                                        </PieChart>
                                    </ResponsiveContainer>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Recent activity */}
                        <Grid item xs={12}>
                            <Card elevation={1} sx={{ borderRadius: 3 }}>
                                <CardHeader title="Recent Inspection Activity" />
                                <CardContent>
                                    {recentSessions.length === 0 ? (
                                        <Typography color="text.secondary">No recent activity.</Typography>
                                    ) : (
                                        <Grid container spacing={1}>
                                            {recentSessions.map((s) => (
                                                <Grid key={s.key} item xs={12} md={6} lg={4}>
                                                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                                                        <CardContent>
                                                            <Stack
                                                                direction="row"
                                                                justifyContent="space-between"
                                                                alignItems="center"
                                                            >
                                                                <Typography variant="subtitle1" fontWeight={700}>
                                                                    {s.CustomerName || `School #${s.CustomerID}`}
                                                                </Typography>
                                                                <Chip label={s.date} size="small" />
                                                            </Stack>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {s.inspectionTitle}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Answers in session: {fmt(s.count)}
                                                            </Typography>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    );
}
